'use client';

import { forwardRef } from 'react';
import { Link } from '@/lib/app-router';
import { cn } from '@/lib/styleUtils';
import type { ActiveSessionIndicatorProps } from './ActiveSessionIndicator.types';
import {
  pillClasses,
  dotBaseClasses,
  dotActiveClasses,
  dotExpiredClasses,
  mobileTextClasses,
  desktopTextClasses,
  garageNameClasses,
  durationClasses,
  separatorClasses,
} from './ActiveSessionIndicator.style';

export const ActiveSessionIndicator = forwardRef<HTMLElement, ActiveSessionIndicatorProps>(
  (
    {
      mobileText,
      desktopText,
      desktopGarageName,
      desktopTicketText,
      desktopTimeText,
      isExpired,
      href,
      onClick,
      ariaLabel,
      className,
    },
    ref
  ) => {
    const dotClass = cn(dotBaseClasses, isExpired ? dotExpiredClasses : dotActiveClasses);

    const content = (
      <>
        {/* Pulsing status dot */}
        <span className={dotClass} aria-hidden="true" />

        {/* Mobile: single text */}
        <span className={mobileTextClasses} data-cy="active-session-indicator-mobile-text">{mobileText}</span>

        {/* Desktop: label + optional garage + optional time */}
        <span className={desktopTextClasses} data-cy="active-session-indicator-desktop-text">{desktopText}</span>

        {desktopGarageName && (
          <>
            <span className={separatorClasses} aria-hidden="true">
              &middot;
            </span>
            <span className={garageNameClasses} data-cy="active-session-indicator-garage">
              {desktopGarageName}
            </span>
          </>
        )}

        {desktopTicketText && (
          <>
            <span className={separatorClasses} aria-hidden="true">
              &middot;
            </span>
            <span className={garageNameClasses} data-cy="active-session-indicator-ticket">
              {desktopTicketText}
            </span>
          </>
        )}

        {desktopTimeText && (
          <>
            <span className={separatorClasses} aria-hidden="true">
              &middot;
            </span>
            <span className={durationClasses} data-cy="active-session-indicator-time">
              {desktopTimeText}
            </span>
          </>
        )}
      </>
    );

    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={cn(pillClasses, className)}
          aria-label={ariaLabel}
          data-cy="active-session-indicator"
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={cn(pillClasses, className)}
        aria-label={ariaLabel}
        onClick={onClick}
        data-cy="active-session-indicator"
      >
        {content}
      </button>
    );
  }
);

ActiveSessionIndicator.displayName = 'ActiveSessionIndicator';
